import React, { useState, useEffect } from 'react';
import { Button } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../redux/store';
import { ToggleColorModeButton } from '../../../theme/ToggleColorModeButton';
import { useResetPasswordForm, useCodeInput } from './hooks';
import { verifyResetCode, resetPassword } from '../store';
import { Step, ResetPasswordFormData } from './types';
import notification from '../../../commonComponents/Notification/Notification';
import {
  AuthContainer,
  FormContainer,
  FormCard,
  Title,
  Subtitle,
  ButtonWrapper,
  PrimaryButton,
} from '../styles';
import { ThemeProvider } from 'styled-components';
import AuthBackground from '../LoginPage/AuthBackground';
import CodeInput from './CodeInput';
import PasswordResetForm from './PasswordResetForm';

const ResetPasswordCodePage: React.FC = () => {
  const [email, setEmail] = useState<string>('');
  const [step, setStep] = useState<Step>('code');
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams] = useSearchParams();
  const { isDarkMode } = useSelector((state: RootState) => state.theme);
  const { isVerifyingCode, isResettingPassword } = useSelector((state: RootState) => state.auth);

  const { control, handleSubmit, formState: { errors } } = useResetPasswordForm();
  const { code, handleCodeChange, handleCodeKeyDown } = useCodeInput(step);

  useEffect(() => {
    const emailFromUrl = searchParams.get('email');
    if (emailFromUrl) {
      setEmail(emailFromUrl);
    } else {
      notification.error('Email Required. Please go back and request a reset code again.');
      navigate('/forgot-password');
    }
  }, [searchParams, navigate]);

  const onVerifyCode = async (): Promise<void> => {
    if (!code || code.length !== 6) return;
    const result = await dispatch(verifyResetCode({ email, code }));
    if (verifyResetCode.fulfilled.match(result)) {
      setStep('password');
    }
  };

  const onSubmitPassword = async (data: ResetPasswordFormData): Promise<void> => {
    const result = await dispatch(resetPassword({ 
      email, 
      password: data.password, 
      confirmPassword: data.confirmPassword,
      navigate 
    }));
    if (resetPassword.rejected.match(result)) {
      const errorMessage = result.payload as string;
      if (errorMessage.toLowerCase().includes('verify') || errorMessage.toLowerCase().includes('otp')) {
        setStep('code');
      }
    }
  };

  // @ts-expect-error - react-hook-form type inference issue with yup resolver
  const handleFormSubmit = handleSubmit(onSubmitPassword);

  const handleBackToLogin = (): void => {
    navigate('/login');
  };

  const theme = { mode: (isDarkMode ? 'dark' : 'light') as 'dark' | 'light' };

  return (
    <ThemeProvider theme={theme}>
      <AuthContainer>
        <AuthBackground isDarkMode={isDarkMode} />
        
        <ToggleColorModeButton
          width="24px"
          height="24px"
          iconButtonProps={{
            position: 'absolute',
            top: '5',
            right: '5',
            zIndex: 10,
          }}
        />

        <FormContainer>
          <FormCard>
            {step === 'code' ? (
              <>
                <Title>Enter Reset Code</Title>
                <Subtitle>
                  Enter the 6-digit code sent to <strong>{email}</strong>
                </Subtitle>
                
                <CodeInput
                  code={code}
                  onCodeChange={handleCodeChange}
                  onKeyDown={handleCodeKeyDown}
                  margin="0"
                />
                
                <ButtonWrapper>
                  <PrimaryButton
                    type="primary"
                    onClick={onVerifyCode}
                    block
                    loading={isVerifyingCode}
                    disabled={code.length !== 6 || isVerifyingCode}
                  >
                    Verify Code
                  </PrimaryButton>
                </ButtonWrapper>
              </>
            ) : (
              <>
                <Title>Reset Your Password</Title>
                <Subtitle>Enter your new password below.</Subtitle>
                
                <PasswordResetForm
                  control={control as any}
                  errors={errors}
                  onSubmit={handleFormSubmit}
                  isLoading={isResettingPassword}
                />
              </>
            )}
            
            <ButtonWrapper>
              <Button
                icon={<FontAwesomeIcon icon={faArrowLeft} />}
                type="link"
                onClick={step === 'code' ? () => navigate('/forgot-password') : handleBackToLogin}
                block
                style={{
                  marginTop: '16px',
                }}
              >
                {step === 'code' ? 'Back to Forgot Password' : 'Back to Login'}
              </Button>
            </ButtonWrapper>
          </FormCard>
        </FormContainer>
      </AuthContainer>
    </ThemeProvider>
  );
};

export default ResetPasswordCodePage;
